'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import seoReviewsDIModule from '@actr/stores/src/modules/seo/reviews';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import ApplicationBanner from '@actr/widgets/src/components/seo/application-banner';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import AllFlights from '@/components/avia/route-from-city/all-flights';
import SearchForm from '@/components/avia/route-from-city/search-form';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import CityAirlinesBlock from '@/components/page-constucts/city-airlines-block';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import ReviewsBlock from '@/components/page-constucts/reviews-block';
import { SeoRoutesFromCity } from '@/services/models/avia/routes-from-city';
import { getContentBlocks } from '@/utils/get-content-blocks';
import { imageObjectJSONLD, organizationJSONLD, postalAdressJSONLD } from '@/utils/schema-org/organization';

import * as Styled from './style.css';

export type BaseRoutesFromCityPageProps = {
  data: SeoRoutesFromCity;
  path: string;
}

const BaseRoutesFromCityPage: FC <BaseRoutesFromCityPageProps & PropsWithChildren> = observer(({ data, children, path }) => {
  const { search_form_title, content, location, breadcrumbs } = data;
  const { info, from_city_routes, from_city_search_form,
    from_city_airlines, from_city_airports, faq,
    feedback, advantages, app_banner } = content || {};
  const aviaSearchForm = useInject(AviaSearchFormStore);

  useEffect(() => {
    const seoSegmentFrom = {
      iata: location.city.iata,
      name: location.city.name,
      airport_aggregation_iata: location.city.iata,
      country: {
        name: location.country.name,
        iata: location.country.iata,
      },
      city: {
        name: location.city.name,
        iata: location.city.iata,
      },
    };
    const segments = [{ ...aviaSearchForm.defaultSegment, from: seoSegmentFrom }];

    aviaSearchForm.stopStore();
    runInAction(() => {
      aviaSearchForm.setSegments(segments);
      aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
    });
  }, [aviaSearchForm, location]);

  const crumbFrom = [{
    title: 'Авиабилеты',
    path: '/',
  }, {
    path: '/routes',
    title: 'Маршрут',
  }, {
    path: `/routes/${path}`,
    title: breadcrumbs,
  }];

  function renderCityAirports() {
    if (!from_city_airports?.list) {
      return null;
    }

    return (
      <ContentBlock className={Styled.Wrapper} tagProps={{ itemScope: true, itemType: 'https://schema.org/ItemList' }}>
        <Text tagName="h3" font="title.x2l" tagProps={{ itemProp: 'name' }}>
          {from_city_airports.title}
        </Text>
        <meta itemProp="description" content={from_city_airports.title} />
        <div className={Styled.MainWrapper} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
          {from_city_airports.list.map((item, index) => (
            <>
              <meta itemProp="position" content={(index + 1).toString()} />
              <a
                itemProp="item"
                className={Styled.Airport}
                href={item.url ? `/${item.url}` : undefined}
                key={index}>
                <div itemProp="name">
                  {item.name}
                </div>
              </a>
            </>
          ))}
        </div>
      </ContentBlock>
    );
  }

  const contentBlocks = content ? [
    {
      key: 'info',
      condition: !!info,
      render: () => info ? (
        <ContentBlock tagProps={{ itemScope: true, itemType: 'https://schema.org/Article' }}>
          <InnerHtmlTextBlock html={info.description} title={info.title} />
        </ContentBlock>
      ) : null
    },
    {
      key: 'from_city_search_form',
      condition: !!from_city_search_form,
      render: () => (
        <SearchForm
          title={from_city_search_form?.title || ''}
          flights={from_city_routes?.list || null}
          fromCity={location.city.name}
        />
      )
    },
    {
      key: 'from_city_airlines',
      condition: !!from_city_airlines,
      render: () => (
        <CityAirlinesBlock airlines={from_city_airlines!} />
      )
    },
    {
      key: 'from_city_airports',
      condition: !!from_city_airports?.list,
      render: renderCityAirports
    },
    {
      key: 'from_city_routes',
      condition: !!from_city_routes,
      render: () => (
        <AllFlights
          title={from_city_routes!.title}
          flights={from_city_routes!.list}
        />
      )
    },
    {
      key: 'faq',
      condition: !!faq?.list,
      render: () => (
        faq ? (
          <Faq list={faq?.list} title={faq.title} />
        ) : null
      )
    },
    {
      key: 'feedback',
      condition: !!feedback?.list,
      render: () => (
        feedback ? (
          <ReviewsBlock title={feedback.title} feedback={feedback?.list} />
        ) : null
      )
    },
    {
      key: 'advantages',
      condition: !!advantages?.list?.enable,
      render: () => (<ServiceAdvantages />)
    },
    {
      key: 'app_banner',
      condition: !!app_banner?.list?.enable,
      render: () => (<ApplicationBanner />)
    },
  ] : [];

  const blocks = getContentBlocks({ content, contentBlocks });

  return (
    <>
      <SearchFormHeader
        title={search_form_title || ''}
      />
      <Container>
        <Breadcrumb crumb={crumbFrom} dynamic={false} />
        <div className={Styled.RoutesFromCity}>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify([
              organizationJSONLD(),
              imageObjectJSONLD(),
              postalAdressJSONLD()]) }}
          />
          {blocks.map(block => block.condition && (
            <React.Fragment key={block.key}>
              {block.render()}
            </React.Fragment>
          ))}
        </div>
        {children}
      </Container>
    </>
  );
});

export default loadDIModules(BaseRoutesFromCityPage, aviaSearchDIModule, seoReviewsDIModule);
